import React, { FC, useEffect, useState } from 'react'
import { Alert, Button, Input, Modal, notification, Table, Typography } from 'antd'
import Icon from '../../components/Icon'
import { useTheme } from 'styled-components'
import StorageForm from '../New/StorageForm'
import { IStorageSystem, IStorageSystemForm } from '../../types/storageSystem'
import { storageApi } from '../../api'
import { ColumnsType } from 'antd/lib/table'

const StorageSytems: FC = () => {
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(true)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isEditLoading, setIsEditLoading] = useState(false)
  const [selectedStorageSystem, setSelectedStorageSystem] = useState<IStorageSystem>()
  const [storageSystems, setStorageSystems] = useState<IStorageSystem[]>([])

  function getRTEColor(rte: number) {
    if (rte < 45) return theme.colors.red
    if (rte < 70) return theme.colors.orange
    return theme.colors.green
  }

  async function fetchStorageSystems() {
    try {
      setIsLoading(true)
      const storageSystemsResponse = await storageApi.fetchStorageSystems()
      setStorageSystems(Array.isArray(storageSystemsResponse) ? storageSystemsResponse : [])
      setIsLoading(false)
    } catch {
      setStorageSystems([])
    }
  }
  function editStorageSystem(storageSystem: IStorageSystem) {
    setSelectedStorageSystem(storageSystem)
    setIsEditModalVisible(true)
  }

  function onStorageSystemFieldChange(_: any, editedStorageSystem: IStorageSystemForm) {
    const { id, name } = selectedStorageSystem || {}
    if (!id || !name) return
    setSelectedStorageSystem({ id, name, ...editedStorageSystem })
  }

  async function saveStorageSystem() {
    try {
      if (selectedStorageSystem) {
        setIsEditLoading(true)
        const storageSystemId = selectedStorageSystem.id
        await storageApi.editStorageSystem(storageSystemId, selectedStorageSystem)
        notification.success({ message: 'Storage System has been edited successfully' })
        setIsEditModalVisible(false)
      }
    } finally {
      setIsEditLoading(false)
      fetchStorageSystems()
    }
  }

  async function deleteStorageSystem(storageSystemId: string) {
    setIsLoading(true)
    await storageApi.deleteStorageSystem(storageSystemId)
    fetchStorageSystems()
  }

  useEffect(() => {
    fetchStorageSystems()
  }, [])

  const columns: ColumnsType<IStorageSystem> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.length - b.name.length,
    },
    {
      title: 'Rt. Power',
      dataIndex: 'ratedPowerKw',
      key: 'ratedPowerKw',
      sorter: (a, b) => a.ratedPowerKw - b.ratedPowerKw,
    },
    {
      title: 'Rt. Energy',
      dataIndex: 'ratedEnergyKw',
      key: 'ratedEnergyKw',
      sorter: (a, b) => a.ratedEnergyKw - b.ratedEnergyKw,
    },
    {
      title: 'Round-Trip Eff.',
      dataIndex: 'netOverallRoundTripEfficiency',
      key: 'netOverallRoundTripEfficiency',
      sorter: (a, b) => a.netOverallRoundTripEfficiency - b.netOverallRoundTripEfficiency,
      render: (rte) => (
        <Typography.Text style={{ color: getRTEColor(rte) }}>
          <b>%{rte}</b>
        </Typography.Text>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
      render: (_, storageSystem) => (
        <div>
          <Button
            className='mr-8'
            icon={<Icon icon='pen-to-square' margin={false} />}
            onClick={() => editStorageSystem(storageSystem)}
          />
          <Button
            danger
            icon={<Icon icon='trash-alt' margin={false} />}
            onClick={() => deleteStorageSystem(storageSystem.id)}
          />
        </div>
      ),
    },
  ]

  return (
    <div>
      <Alert
        type='info'
        message={<Typography.Title level={4}>Storage Sytems</Typography.Title>}
        description='Saved storage systems for different simulations. Each storage systems parameters can be edited for different simulations. '
      />

      <Input.Search className='my-24' placeholder='Search' onChange={fetchStorageSystems} />
      <Table
        rowKey='id'
        rowClassName='cursor-pointer'
        columns={columns}
        dataSource={storageSystems}
        loading={isLoading}
        bordered
      />

      <Modal
        title={selectedStorageSystem?.name}
        visible={isEditModalVisible}
        confirmLoading={isEditLoading}
        width={1200}
        okText='Save'
        closable={false}
        destroyOnClose
        onCancel={() => setIsEditModalVisible(false)}
        onOk={saveStorageSystem}
      >
        <StorageForm
          initialValues={selectedStorageSystem}
          onValuesChange={onStorageSystemFieldChange}
        />
      </Modal>
    </div>
  )
}

export default StorageSytems
